import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'

import { useAppDispatch } from 'common/hooks/redux'
import { debugPusher, useMultipleChannels } from 'features/Schedule/pusher/MultiplePusherProvider'

import Popup from 'common/components/Popup'
import { PUSHER_EVENTS } from 'features/Home/constants/infoConstants'
import {
  applySchedulePusherBatch,
  createEmptySchedulePusherBatch,
  isSchedulePusherBatchEmpty,
  mergePatientCreateIntoBatch,
  mergePatientDeleteIntoBatch,
  mergePatientLocationIntoBatch,
  mergePatientStatusIntoBatch,
  mergePatientUpdateIntoBatch,
  mergeRoomStatusIntoBatch,
  SCHEDULE_PUSHER_BATCH_DEBOUNCE_MS,
  type SchedulePusherPendingBatch,
} from 'features/Schedule/pusher/schedulePusherBatch'
import {
  isRedundantPusherPatientDelete,
  isRedundantPusherPatientStatus,
  isRedundantPusherPatientUpsert,
  isRedundantPusherRoomStatus,
} from 'features/Schedule/pusher/schedulePusherEcho'
import type {
  MassUpdateEvent,
  PatientDeleteEvent,
  PatientLocationUpdateEvent,
  PatientStatusUpdateEvent,
  PatientUpdateEvent,
  RoomStatusUpdateEvent,
} from 'features/Schedule/pusher/type'

export const useSchedulePusher = (date: string) => {
  const { channels, bookingIds } = useMultipleChannels()
  const { siteId } = useParams()
  const navigate = useNavigate()
  const scheduleState = useSelector((state: any) => state.schedule)
  const scheduleQueriesRef = useRef(scheduleState?.queries)

  const dispatch = useAppDispatch()
  const pendingBatchRef = useRef<SchedulePusherPendingBatch>(createEmptySchedulePusherBatch())
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const siteIdRef = useRef(siteId)
  const dateRef = useRef(date)

  useEffect(() => {
    scheduleQueriesRef.current = scheduleState?.queries
  }, [scheduleState?.queries])

  useEffect(() => {
    siteIdRef.current = siteId
    dateRef.current = date
  }, [siteId, date])

  const flushPendingBatch = useCallback(() => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current)
      flushTimeoutRef.current = null
    }

    const batch = pendingBatchRef.current
    if (isSchedulePusherBatchEmpty(batch)) return

    const currentSiteId = siteIdRef.current
    const currentDate = dateRef.current
    if (!currentSiteId || !currentDate) return

    if (debugPusher) {
      console.log('Flushing batched schedule Pusher updates', {
        updates: batch.patientUpdates.size,
        creates: batch.patientCreates.size,
        deletes: batch.patientDeletes.size,
        locations: batch.locationUpdates.size,
        statuses: batch.statusUpdates.size,
        rooms: batch.roomStatusUpdates.size,
      })
    }

    applySchedulePusherBatch(
      dispatch,
      currentSiteId,
      currentDate,
      batch,
      scheduleQueriesRef.current || {},
    )

    pendingBatchRef.current = createEmptySchedulePusherBatch()
  }, [dispatch])

  const scheduleBatchFlush = useCallback(() => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current)
    }

    flushTimeoutRef.current = setTimeout(() => {
      flushTimeoutRef.current = null
      flushPendingBatch()
    }, SCHEDULE_PUSHER_BATCH_DEBOUNCE_MS)
  }, [flushPendingBatch])

  const enqueueBatchUpdate = useCallback(() => {
    scheduleBatchFlush()
  }, [scheduleBatchFlush])

  const massUpdate = useCallback(
    (event: MassUpdateEvent) => {
      if (debugPusher) console.log('MASS_UPDATE event received:', event)
      const { day, shouldUpdate } = event
      if (day === date && shouldUpdate) {
        Popup.showInfo(
          {
            title: 'Schedule updated',
            message: 'You have a mass update. Would you like to refresh the page?',
            mainButtonText: 'Refresh',
            secondButtonText: 'No',
          },
          () => navigate(0),
        )
      }
    },
    [date, navigate],
  )

  const patientUpdate = useCallback(
    (event: PatientUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_UPDATED event received:', event)
      const { data, shouldUpdate } = event
      if (!shouldUpdate || !data) return
      if (isRedundantPusherPatientUpsert(data)) return

      mergePatientUpdateIntoBatch(pendingBatchRef.current, data)
      enqueueBatchUpdate()
    },
    [enqueueBatchUpdate],
  )

  const patientCreate = useCallback(
    (event: PatientUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_CREATE event received:', event)
      const { data, shouldUpdate } = event
      if (!shouldUpdate || !data) return
      if (isRedundantPusherPatientUpsert(data)) return

      mergePatientCreateIntoBatch(pendingBatchRef.current, data)
      enqueueBatchUpdate()
    },
    [enqueueBatchUpdate],
  )

  const patientDelete = useCallback(
    (event: PatientDeleteEvent) => {
      if (debugPusher) console.log('PATIENT_DELETE event received:', event)
      const { appointment_id, shouldUpdate } = event
      if (!shouldUpdate) return
      if (isRedundantPusherPatientDelete(appointment_id)) return

      mergePatientDeleteIntoBatch(pendingBatchRef.current, appointment_id)
      enqueueBatchUpdate()
    },
    [enqueueBatchUpdate],
  )

  const patientLocationUpdate = useCallback(
    (event: PatientLocationUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_LOCATION_UPDATE event received:', event)
      const { appointment_id, room_id, shouldUpdate } = event
      if (!shouldUpdate) return

      mergePatientLocationIntoBatch(pendingBatchRef.current, appointment_id, room_id)
      enqueueBatchUpdate()
    },
    [enqueueBatchUpdate],
  )

  const patientStatusUpdate = useCallback(
    (event: PatientStatusUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_STATUS_UPDATE event received:', event)
      const { appointment_id, shouldUpdate, status_code, temperature } = event
      if (!shouldUpdate) return
      if (isRedundantPusherPatientStatus(appointment_id, status_code, temperature)) return

      mergePatientStatusIntoBatch(pendingBatchRef.current, appointment_id, status_code, temperature)
      enqueueBatchUpdate()
    },
    [enqueueBatchUpdate],
  )

  const roomStatusUpdate = useCallback(
    (event: RoomStatusUpdateEvent) => {
      if (debugPusher) console.log('ROOM_STATUS_UPDATE event received:', event)
      const { room_id, room_status_code, shouldUpdate } = event
      if (!shouldUpdate) return
      if (isRedundantPusherRoomStatus(room_id, room_status_code)) return

      mergeRoomStatusIntoBatch(pendingBatchRef.current, room_id, room_status_code)
      enqueueBatchUpdate()
    },
    [enqueueBatchUpdate],
  )

  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current)
      }
      flushPendingBatch()
    }
  }, [flushPendingBatch])

  useEffect(() => {
    if (!channels.size || !bookingIds.length) {
      if (debugPusher)
        console.log('No channels or bookingIds available for event binding', {
          channelsSize: channels.size,
          bookingIdsLength: bookingIds.length,
        })
      return
    }

    if (debugPusher) console.log('Binding events to channels, count:', channels.size)

    channels.forEach((channel, channelName) => {
      if (debugPusher) console.log(`Binding events to channel: ${channelName}`)

      channel.bind(PUSHER_EVENTS.MASS_UPDATE, massUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_UPDATED, patientUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_CREATE, patientCreate)
      channel.bind(PUSHER_EVENTS.PATIENT_DELETE, patientDelete)
      channel.bind(PUSHER_EVENTS.PATIENT_LOCATION_UPDATE, patientLocationUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_STATUS_UPDATE, patientStatusUpdate)
      channel.bind(PUSHER_EVENTS.ROOM_STATUS_UPDATE, roomStatusUpdate)
    })

    return () => {
      if (debugPusher) console.log('Unbinding events from channels')
      channels.forEach((channel, channelName) => {
        if (debugPusher) console.log(`Unbinding events from channel: ${channelName}`)

        channel.unbind(PUSHER_EVENTS.MASS_UPDATE, massUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_UPDATED, patientUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_CREATE, patientCreate)
        channel.unbind(PUSHER_EVENTS.PATIENT_DELETE, patientDelete)
        channel.unbind(PUSHER_EVENTS.PATIENT_LOCATION_UPDATE, patientLocationUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_STATUS_UPDATE, patientStatusUpdate)
        channel.unbind(PUSHER_EVENTS.ROOM_STATUS_UPDATE, roomStatusUpdate)
      })
    }
  }, [
    channels,
    bookingIds.length,
    massUpdate,
    patientUpdate,
    patientCreate,
    patientDelete,
    patientLocationUpdate,
    patientStatusUpdate,
    roomStatusUpdate,
  ])
}
